import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardHeader,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    Alert,
    AlertTitle,
    Divider
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const CustomClaimsWorkflow: React.FC = () => {
    return (
        <Box id="workflow-custom-claims" sx={{ scrollMarginTop: '80px', mt: 3 }}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Adding Custom Claims from Directory Extensions</Typography>
                        </Box>
                    }
                    action={
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => {
                                const element = document.getElementById('workflow-navigation');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                        >
                            Back to Workflows
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    <Typography variant="body2" paragraph>
                        This workflow guides you through adding custom claims to tokens, using directory extensions from Entra Connect.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Prerequisites:</Typography>
                        <List dense disablePadding>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="Directory extensions synced from Entra Connect or custom extensions created in your tenant" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="Admin permissions to create claims mapping policies and modify applications" />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 1: Identify Directory Extensions
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                First, identify which directory extensions you want to include in tokens:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Tools > Directory Extensions Manager"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select the 'AD Directory Extensions' tab to view directory extensions synced from Entra Connect"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Browse the available extensions and note the full extension name (e.g., extension_ab123cde45678f_employeeType)"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Copy the Claims Mapping Format shown in the details panel for use in Step 2"
                                        secondary="Especially note the exact extension ID which will be used in the claims mapping policy"
                                    />
                                </ListItem>
                            </List>

                            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>Important</AlertTitle>
                                <Typography variant="body2">
                                    Not all synchronized AD attributes can be used in claims mapping policies. For best results,
                                    use the standard Entra ID extension attributes (extensionAttribute1-15) or other attributes
                                    that are part of the base Entra ID schema.
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 2: Create Claims Mapping Policy
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Next, create a claims mapping policy to include these extensions in tokens:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Tools > Claims Mapping Policy Tool"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'New Policy' to create a new Claims Mapping Policy"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Enter a descriptive name for your policy (e.g., 'CustomADAttributes')"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Use the following JSON template for your policy:" />
                                </ListItem>
                            </List>

                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, my: 2, fontFamily: 'monospace', overflow: 'auto' }}>
                                <pre style={{ margin: 0 }}>
                                    {`{
  "ClaimsMappingPolicy": {
    "Version": 1,
    "IncludeBasicClaimSet": "true",
    "ClaimsSchema": [
      {
        "Source": "user",
        "ID": "extension_<extensionID>_<attributeName>",
        "JwtClaimType": "<customClaimName>"
      },
      {
        "Source": "user",
        "ID": "extensionAttribute1",
        "JwtClaimType": "customAttribute1"
      }
    ]
  }
}`}
                                </pre>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Replace the placeholder values:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                    <ListItemText
                                        primary="extension_<extensionID>_<attributeName>: Use the full extension name you noted from Step 1"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                    <ListItemText
                                        primary="<customClaimName>: Choose a descriptive name for this claim as it will appear in tokens"
                                    />
                                </ListItem>
                            </List>

                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Validate JSON' to verify your policy format"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Create Policy' to save your claims mapping policy"
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 3: Assign Policy to Application
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Now, assign this policy to the application that needs these custom claims:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="In the Claims Mapping Policy Tool, select your newly created policy"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Assign' button to assign this policy to an application"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Search for and select the application that should receive these custom claims"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Confirm the assignment"
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 4: Enable acceptMappedClaims in Application Manifest
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Finally, configure the application to accept mapped claims:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Tools > Application Manifest Editor"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select the same application you assigned the claims mapping policy to"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary={`Modify the manifest to add "acceptMappedClaims": true to the JSON`}
                                        secondary="Add this at the root level of the JSON object, alongside properties like 'appId' and 'displayName'"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Validate JSON' to verify your changes"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Save Changes' to update the application manifest"
                                    />
                                </ListItem>
                            </List>

                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, my: 2, fontFamily: 'monospace', overflow: 'auto' }}>
                                <pre style={{ margin: 0 }}>
                                    {`{
  "id": "11111111-1111-1111-1111-111111111111",
  "appId": "22222222-2222-2222-2222-222222222222",
  "displayName": "My Application",
  "acceptMappedClaims": true,
  // ... other properties
}`}
                                </pre>
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 5: Verify Custom Claims in Tokens
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                To verify that your custom claims are included in tokens:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to the App Tester for your application"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Use the 'Authenticate As' feature to request a token"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Examine the token in the token panel at the bottom of the screen"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Look for your custom claims in the decoded token payload"
                                        secondary="The claims will appear with the names you specified in the JwtClaimType property"
                                    />
                                </ListItem>
                            </List>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    If your custom claims don't appear in the token, verify that:
                                </Typography>
                                <List dense sx={{ pl: 2, mt: 1 }}>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 24 }}><CircleIcon sx={{ fontSize: 6 }} /></ListItemIcon>
                                        <ListItemText primary="The extension attribute exists and contains data for the signed-in user" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 24 }}><CircleIcon sx={{ fontSize: 6 }} /></ListItemIcon>
                                        <ListItemText primary="The claims mapping policy is correctly assigned to the application" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 24 }}><CircleIcon sx={{ fontSize: 6 }} /></ListItemIcon>
                                        <ListItemText primary="The application has acceptMappedClaims set to true" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 24 }}><CircleIcon sx={{ fontSize: 6 }} /></ListItemIcon>
                                        <ListItemText primary="The extension attribute is compatible with claims mapping (generally, extensionAttribute1-15 work best)" />
                                    </ListItem>
                                </List>
                            </Alert>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CustomClaimsWorkflow;